import { useEffect, useRef, useState } from 'react'
import {
  LAYI_NOTIFICATIONS_CHANGED_EVENT,
  LAYI_NOTIFICATIONS_KEY,
  LAYI_NOTIFICATIONS_OUTCOMES,
  areLayiNotificationsOn,
  disableLayiNotifications,
  enableLayiNotifications
} from './layiNotifications'

// The one LAYI notifications switch, behind the user menu row and the invite card. Its state follows the
// opt-in key, which the other switch, the per-user check on load or another tab can change. `toggle` is the
// click handler: turning on asks for the browser's permission inside the click (layiNotifications.js stores
// the answer). An activation, unless this user hid the setup in this browser, or a permission the browser
// denies, needs the LAYI notifications modal: the caller opens it through `onNeedsModal({ blocked })`, even
// after unmounting (the menu closes on a click elsewhere while the browser asks). A second click, or a held
// key, while the browser asks is ignored: one answer, one report, one modal.
export default function useLayiNotificationsSwitch({ t, userId, source, onNeedsModal }) {
  const [on, setOn] = useState(areLayiNotificationsOn)
  const mountedRef = useRef(false)
  const pendingRef = useRef(false)
  const onNeedsModalRef = useRef(onNeedsModal)
  onNeedsModalRef.current = onNeedsModal

  useEffect(() => {
    mountedRef.current = true
    const sync = () => setOn(areLayiNotificationsOn())
    const syncFromOtherTab = event => {
      if (event.key === null || event.key === LAYI_NOTIFICATIONS_KEY) sync()
    }
    sync()
    window.addEventListener(LAYI_NOTIFICATIONS_CHANGED_EVENT, sync)
    window.addEventListener('storage', syncFromOtherTab)
    return () => {
      mountedRef.current = false
      window.removeEventListener(LAYI_NOTIFICATIONS_CHANGED_EVENT, sync)
      window.removeEventListener('storage', syncFromOtherTab)
    }
  }, [])

  const toggle = () => {
    if (on) {
      disableLayiNotifications({ userId, source })
      setOn(false)
      return
    }
    if (pendingRef.current) return
    pendingRef.current = true
    enableLayiNotifications({
      userId,
      source,
      text: { title: t('layi_notification_title'), body: t('layi_notification_body') }
    }).then(outcome => {
      pendingRef.current = false
      if (mountedRef.current) setOn(areLayiNotificationsOn())
      if (outcome === LAYI_NOTIFICATIONS_OUTCOMES.SETUP || outcome === LAYI_NOTIFICATIONS_OUTCOMES.BLOCKED) {
        onNeedsModalRef.current({ blocked: outcome === LAYI_NOTIFICATIONS_OUTCOMES.BLOCKED })
      }
    })
  }

  return { on, toggle }
}
